'use client'

import * as React from 'react'

import { BaseHubImage } from 'basehub/next-image'
import { HomepageFeatureFragment } from '..'
import { useMedia } from '~/hooks/use-media'
import { clsx } from 'clsx'

import s from './feature-list.module.scss'

export const FeatureList = ({
  features,
}: {
  features: HomepageFeatureFragment[]
}) => {
  const feature = features[0]
  const highlighterRef = React.useRef<HTMLSpanElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const imageRef = React.useRef<HTMLImageElement>(null)
  const isTabletOrMobile = useMedia('(max-width: 1279px)')
  const [locked, setLocked] = React.useState(false)
  const [highlightedFeatureImageSrc, setHighlightedFeatureImageSrc] =
    React.useState(feature?.image.url ?? '')
  const addRotation = (element: HTMLLIElement) => {
    if (!s['feature-item-rotated']) return
    element.classList.add(s['feature-item-rotated'])
  }
  const removeRotation = (element: HTMLLIElement) => {
    if (!s['feature-item-rotated']) return
    element.classList.remove(s['feature-item-rotated'])
  }

  const highlightSelectedFeature = React.useCallback(
    (el: HTMLElement) => {
      if (!highlighterRef.current) return

      const top = el.offsetTop + 3
      const height = el.offsetHeight - 7
      const width = el.offsetWidth
      const left = el.offsetLeft
      highlighterRef.current.style.top = `${top}px`
      highlighterRef.current.style.left = `${left}px`
      highlighterRef.current.style.height = `${height}px`
      highlighterRef.current.style.width = `${width}px`
      highlighterRef.current.style.visibility = `visible`

      const listItems = listRef.current?.querySelectorAll('li')
      ;(listItems ?? []).forEach((item) => {
        if (item !== el) {
          if (isTabletOrMobile) {
            item
              .querySelectorAll<HTMLSpanElement>('span[data-char-copy]')
              .forEach((el) => el?.style.removeProperty('color'))
          }
          removeRotation(item)
        } else {
          addRotation(item)
          if (isTabletOrMobile) {
            el.querySelectorAll<HTMLSpanElement>(
              'span[data-char-copy]'
            ).forEach((el) => {
              el?.style.setProperty('color', 'var(--color-bright)')
            })
          }
        }
      })

      if (!el.dataset.featureSrc) return
      setHighlightedFeatureImageSrc(el.dataset.featureSrc)
    },
    [isTabletOrMobile]
  )

  React.useEffect(() => {
    const handleScroll = () => {
      if (locked) {
        const rect = highlighterRef.current?.getBoundingClientRect()
        if (!rect) return
        const targetY = window.innerHeight / 3
        const highlightedItemIsInAThirdOfTheScreen =
          rect.top < targetY && rect.bottom > targetY
        const highlightedItemIsOutOfTheScreen =
          rect.top > window.innerHeight - 20 || rect.bottom < 20
        if (
          highlightedItemIsInAThirdOfTheScreen ||
          highlightedItemIsOutOfTheScreen
        ) {
          setLocked(false)
        } else {
          return
        }
      }
      if (!listRef.current) return

      const listItems = listRef.current.querySelectorAll('li')
      const itemThatIsInTheMiddleOfTheScreen = Array.from(listItems).find(
        (item) => {
          const rect = item.getBoundingClientRect()
          const targetY = window.innerHeight / 3
          return rect.top < targetY && rect.bottom > targetY
        }
      )

      if (!itemThatIsInTheMiddleOfTheScreen) return
      highlightSelectedFeature(itemThatIsInTheMiddleOfTheScreen)
    }
    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [locked, highlightSelectedFeature, isTabletOrMobile])

  return (
    <div className="flex flex-col lg:flex-row lg:items-start mt-8 gap-14 overflow-x-clip xl:overflow-x-visible">
      <ul className="relative lg:max-w-[336px] w-full shrink-0" ref={listRef}>
        {features.map((feature) => (
          <FeatureEntry
            key={feature._id}
            data-feature-src={feature.image.url}
            onClick={(e) => {
              // scroll into 1/3 of the screen
              setLocked(true)
              highlightSelectedFeature(e.currentTarget)
              if (e.currentTarget) {
                e.currentTarget.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                })
              }
            }}
            onMouseEnter={(e) => {
              // scroll into 1/3 of the screen
              if (isTabletOrMobile) return
              highlightSelectedFeature(e.currentTarget)
            }}
          >
            {feature._title}
          </FeatureEntry>
        ))}

        <span
          ref={highlighterRef}
          className="inset-y-1 -inset-x-0 bg-dark-base-dark absolute transition-top duration-150 ease-out rounded-sm pointer-events-none invisible left-0"
        />
      </ul>

      {!!feature && (
        <BaseHubImage
          className="sticky w-3/4 select-none sm:w-1/2 lg:w-full rounded-md border border-dark-border-solid bottom-6 lg:bottom-auto lg:top-[calc(24px+var(--header-height))] z-10"
          ref={imageRef}
          src={highlightedFeatureImageSrc}
          alt={feature.image.alt ?? ''}
          width={feature?.image.height}
          height={feature?.image.height}
        />
      )}
    </div>
  )
}

const FeatureEntry = (props: React.JSX.IntrinsicElements['li']) => {
  const isTabletOrMobile = useMedia('(max-width: 1279px)')
  const children: typeof props.children extends string
    ? string[]
    : typeof props.children =
    typeof props.children === 'string'
      ? props.children.split('').map((char) => (char === ' ' ? '\u00A0' : char))
      : props.children

  return (
    <li
      {...props}
      className={clsx(
        s['feature-item'],
        'border-b border-dark-border-solid p-4 relative text-xl font-medium group text-dark-muted cursor-default z-10'
      )}
      onMouseEnter={(e) => {
        props.onMouseEnter?.(e)
        if (!s['feature-item-rotated'] || isTabletOrMobile) return
        e.currentTarget.classList.add(s['feature-item-rotated'])
      }}
    >
      {typeof props.children === 'string'
        ? (children as string[]).map((char: string, index: number) => (
            <p key={char + index} className="inline-flex">
              <span
                style={{
                  ['--total' as string]: (children as string[]).length,
                  ['--index' as string]: index + 1,
                }}
              >
                {char}
              </span>
              <span
                className={s['char-copy']}
                data-char-copy
                style={{
                  ['--total' as string]: (children as string[]).length,
                  ['--index' as string]: index + 1,
                }}
              >
                {char}
              </span>
            </p>
          ))
        : props.children}
    </li>
  )
}
